import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { getPaymentList } from 'Containers/RocketPay/actions';
import { RocketPayReport } from 'Components/RocketPay';

interface Props {
  isActive?: boolean;
}

function RocketPayReportContainer({ isActive = false }: Props) {
  const dispatch = useDispatch();
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchPayments = useCallback(async () => {
    if (!firstCompanyId) {
      setPayments([]);
      setHasLoaded(false);
      return;
    }

    setIsLoading(true);

    try {
      const allPayments = [];
      let page = 1;
      let lastPage = false;
      let requestFailed = false;

      while (!lastPage) {
        const response = await dispatch(getPaymentList(firstCompanyId, page, '-created_at', {}, false, 100));

        if (!response) {
          requestFailed = true;
          lastPage = true;
        } else if (!response?.data?.length) {
          lastPage = true;
        } else {
          allPayments.push(...response.data);
          lastPage = response?.meta?.current_page >= response?.meta?.last_page || response.data.length < 100;
          page += 1;
        }
      }

      if (!requestFailed) {
        setPayments(allPayments);
        setHasLoaded(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, firstCompanyId]);

  useEffect(() => {
    setHasLoaded(false);
    setPayments([]);
  }, [firstCompanyId]);

  useEffect(() => {
    if (isActive && !hasLoaded) {
      fetchPayments();
    }
  }, [fetchPayments, hasLoaded, isActive]);

  return <RocketPayReport payments={payments} isLoading={isLoading} />;
}

const RocketPayReportContainerMemo = memo(RocketPayReportContainer, areEqual);

export { RocketPayReportContainerMemo as RocketPayReport };
